/**
 * Slice LEVI-VOICE-01 · Deterministic voice-ranking function.
 *
 * Pure function. Given the browser's available voices and the user's
 * semantic preferences, returns an ordered list of candidate voices
 * (highest score first). No side effects, no localStorage, no
 * DOM access. Deterministic — the same input always produces the
 * same output.
 */

// ---------- Curated seed lists ----------
// These are seed hints for the ranking scorer, not exclusive filters.
// Voice availability varies wildly across browsers, OSes, and devices,
// so the ranker uses these as one signal among several.
const FRIENDLY_FEMALE_NAMES = [
  "samantha", "karen", "victoria", "moira", "tessa", "fiona",
  "susan", "allison", "ava", "kate", "serena",
  "google us english", "google uk english female",
  "microsoft aria", "microsoft jenny", "microsoft zira",
  "paulina", "mónica", "monica", "esperanza", "helena",
  "google español", "microsoft sabina", "microsoft helena",
];

const FRIENDLY_MALE_NAMES = [
  "daniel", "alex", "fred", "tom", "aaron", "arthur", "oliver",
  "google uk english male", "microsoft guy", "microsoft davis",
  "microsoft mark", "microsoft ryan",
  "jorge", "diego", "juan", "carlos", "enrique",
  "microsoft pablo", "microsoft dalia",
];

const ROBOTIC_PATTERNS = [
  "espeak", "e-speak", "pico", "compact", "flite",
];

// ---------- Helpers ----------
const _name = (v) => ((v && v.name) || "").toLowerCase();
const _lang = (v) => ((v && v.lang) || "").toLowerCase();

const _matchesAny = (name, patterns) =>
  patterns.some((p) => name.includes(p.toLowerCase()));

// ---------- Scorer ----------
const scoreVoice = (voice, prefs) => {
  if (!voice) return -Infinity;
  const name = _name(voice);
  const lang = _lang(voice);
  const wantLang = (prefs && prefs.language) || "en";
  const wantGender = (prefs && prefs.gender_preference) || "no_preference";

  let score = 0;

  // Language — hard filter via a very large penalty rather than a
  // boolean cutoff so tests can inspect the full ordering.
  if (lang.startsWith(wantLang)) {
    score += 100;
    // Regional preference: en-US and es-MX are common defaults on
    // North American devices where JobClose ships first.
    if ((wantLang === "en" && lang === "en-us") ||
        (wantLang === "es" && (lang === "es-mx" || lang === "es-us"))) {
      score += 30;
    }
  } else {
    score -= 1000;
  }

  // Local-service voices tend to be higher quality than remote fallbacks.
  if (voice.localService === true) score += 20;

  // Friendly-voice seed matches.
  const isFemaleName = _matchesAny(name, FRIENDLY_FEMALE_NAMES);
  const isMaleName = _matchesAny(name, FRIENDLY_MALE_NAMES);
  if (isFemaleName || isMaleName) score += 15;

  // Gender preference — voice-name gender detection is inherently
  // imperfect; we treat it as a preference, not a guarantee. The
  // bonus is deliberately larger than the regional bonus (+30) so
  // an explicit gender preference wins over an en-US vs en-GB
  // regional-variant tiebreak. A mismatched gender does NOT get a
  // penalty — it just doesn't get the bonus.
  if (wantGender === "friendly_female" && isFemaleName) score += 40;
  if (wantGender === "friendly_male" && isMaleName) score += 40;

  // Known robotic / low-quality patterns.
  if (_matchesAny(name, ROBOTIC_PATTERNS)) score -= 50;

  return score;
};

/**
 * Rank a list of available voices against the user's semantic
 * preferences. Returns a NEW array sorted highest-score-first.
 * Voices that fail the language filter are excluded from the result.
 */
export const rankVoices = (voices, prefs) => {
  if (!Array.isArray(voices) || voices.length === 0) return [];
  const p = prefs || {};
  const scored = voices
    .map((v) => ({ voice: v, score: scoreVoice(v, p) }))
    .filter((x) => x.score > -500); // exclude language mismatches
  scored.sort((a, b) => b.score - a.score);
  return scored.map((x) => x.voice);
};

/**
 * Convenience: choose ONE best voice for the given prefs, or null if
 * no voice in the available list matches the requested language.
 * Callers use the graceful fallback chain in leviSpeech.js when this
 * returns null.
 */
export const chooseVoice = (voices, prefs) => {
  const ranked = rankVoices(voices, prefs);
  return ranked.length ? ranked[0] : null;
};

// Exported for tests only. Do not consume in production code.
export const __ranking_internals = {
  FRIENDLY_FEMALE_NAMES,
  FRIENDLY_MALE_NAMES,
  ROBOTIC_PATTERNS,
  scoreVoice,
};
